import { useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import logoutHandler from 'helpers/logoutHandler';
import axiosInstance from '../helpers/http/axiosInstance';
import refreshToken from '../helpers/http/refreshToken';

/**
 * @typedef {import('axios').AxiosResponse} FetchResponse
 */

/**
 * @typedef {Object} FetchOptions
 * @property {'get'|'post'|'put'|'delete'} [method]
 * @property {string} url
 * @property {any} [data]
 * @property {Record<string, any>} [params]
 * @property {boolean} [showSuccess]
 * @property {boolean} [showError]
 * @property {AbortSignal} [signal]
 */

/**
 * @typedef {import('axios').AxiosRequestConfig & FetchOptions} FetchConfig
 */

function handleRefreshToken({ config, url, method, data, params, onFetchSuccess, resolve, reject, fetch }) {
    refreshToken(() => {
        fetch({
            ...config,
            url,
            method,
            data,
            params,
            onFetchSuccess,
        })
            .then(resolve)
            .catch(reject);
    });
}

/**
 * @param {FetchConfig} defaultConfig
 */
function useOnFetch(defaultConfig) {
    const abortRef = useRef(new AbortController());

    /**
     * @param {FetchConfig} config
     * @returns {Promise<FetchResponse>}
     */
    const fetch = useCallback(({ url, method = 'get', data, params, onFetchSuccess = null, ...config }) => {
        return new Promise((resolve, reject) => {
            axiosInstance({
                url: `${process.env.REACT_APP_API_BASE_URL}/api/v1${url}`,
                method,
                data,
                params,
                signal: config?.isDisableAbort ? undefined : abortRef.current.signal,
                ...defaultConfig,
                ...config,
            })
                .then((res) => {
                    if (res?.data?.code === 10003) {
                        handleRefreshToken({ config, url, method, data, params, onFetchSuccess, resolve, reject, fetch });

                        return;
                    } else if (res?.data?.message === 'unauthorized') {
                        logoutHandler();
                    }
                    if (onFetchSuccess) {
                        resolve(onFetchSuccess(res?.data));
                        return;
                    }
                    resolve(res?.data);
                })
                .catch((error) => {
                    if (axios.isCancel(error)) {
                        console.warn('Request cancelled', error.message);
                    } else {
                        console.error('Fetch error:', error);
                    }
                    reject(error);
                });
        });
    }, []);

    fetch.abort = () => {
        if (abortRef.current) {
            abortRef.current.abort();
            abortRef.current = new AbortController();
        }
    };

    useEffect(() => {
        return () => fetch.abort();
    }, [fetch]);

    return fetch;
}

export default useOnFetch;
